import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Toast from '../../components/Toast';
import EditOrderModal from './EditOrderModal';
import NewOrderModal from './NewOrderModal';
import ConfirmationModal from './ConfirmationModal';
import { formatDateSriLanka } from '../../utils/dateUtils';
import './AdminPanel.css';

function AdminPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, processing, completed, cancelled
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, total-asc, total-desc
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [areaFilter, setAreaFilter] = useState('all');
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, orderId: null });
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState(false);

  const { username, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      setToast({ message: 'Logged out successfully', type: 'success' });
      setTimeout(() => {
        navigate('/admin-login');
      }, 500);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOrders();
      setOrders(data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
      setToast({ message: 'Error loading orders', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Silently refresh orders without showing loading spinner (for delete/update operations)
  const refreshOrdersSilently = async () => {
    try {
      const data = await apiService.getOrders();
      setOrders(data || []);
    } catch (err) {
      console.error('Error refreshing orders:', err);
      setToast({ message: 'Error refreshing orders', type: 'error' });
    }
  };

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleSaveOrder = async (updatedData) => {
    try {
      await apiService.updateOrder(selectedOrder.id, updatedData);
      setToast({ message: 'Order updated successfully!', type: 'success' });
      await refreshOrdersSilently(); // Silently refresh without showing loader
      handleModalClose();
    } catch (err) {
      console.error('Error updating order:', err);
      setToast({ message: 'Failed to update order', type: 'error' });
      throw err;
    }
  };

  const handleDeleteClick = (orderId) => {
    setDeleteConfirmation({ isOpen: true, orderId });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation.orderId) {
      try {
        await apiService.deleteOrder(deleteConfirmation.orderId);
        setToast({ message: 'Order deleted successfully!', type: 'success' });
        await refreshOrdersSilently(); // Silently refresh without showing loader
      } catch (err) {
        console.error('Error deleting order:', err);
        setToast({ message: 'Failed to delete order', type: 'error' });
      }
    }
    setDeleteConfirmation({ isOpen: false, orderId: null });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, orderId: null });
  };

  // Bulk selection handlers
  const handleSelectOrder = (orderId) => {
    const newSelected = new Set(selectedOrderIds);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrderIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedOrderIds.size === filteredOrders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(filteredOrders.map(order => order.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedOrderIds.size > 0) {
      setBulkDeleteConfirmation(true);
    }
  };

  const handleConfirmBulkDelete = async () => {
    try {
      await Promise.all(
        Array.from(selectedOrderIds).map(orderId => apiService.deleteOrder(orderId))
      );
      setToast({ message: `${selectedOrderIds.size} order(s) deleted successfully!`, type: 'success' });
      setSelectedOrderIds(new Set());
      setBulkDeleteConfirmation(false);
      await refreshOrdersSilently(); // Silently refresh without showing loader
    } catch (err) {
      console.error('Error deleting orders:', err);
      setToast({ message: 'Failed to delete some orders', type: 'error' });
    }
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteConfirmation(false);
  };

  const handleNewOrderClick = () => {
    setSelectedOrder(null);
    setIsNewOrderModalOpen(true);
  };

  const handleNewOrderModalClose = () => {
    setIsNewOrderModalOpen(false);
  };

  const handleCreateOrder = async (orderData) => {
    try {
      await apiService.createOrder(orderData);
      setToast({ message: 'Order created successfully!', type: 'success' });
      await refreshOrdersSilently(); // Silently refresh without showing loader
      handleNewOrderModalClose();
    } catch (err) {
      console.error('Error creating order:', err);
      setToast({ message: 'Failed to create order', type: 'error' });
      throw err;
    }
  };

  const handleClearFilters = () => {
    setFilter('all');
    setSearchTerm('');
    setSortBy('date-desc');
    setDateFrom('');
    setDateTo('');
    setAreaFilter('all');
    setToast({ message: 'Filters cleared', type: 'success' });
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return 'status-pending';
      case 'processing':
        return 'status-processing';
      case 'completed':
        return 'status-completed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  const formatDate = (dateString) => {
    return formatDateSriLanka(dateString, 'date');
  };

  const formatTime = (dateString) => {
    return formatDateSriLanka(dateString, 'time');
  };

  // Filter and sort orders
  let filteredOrders = orders.filter((order) => {
    const matchStatusFilter = filter === 'all' || (order.status || '').toLowerCase() === filter.toLowerCase();
    
    const matchSearch = searchTerm === '' || 
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.phone1?.includes(searchTerm) ||
      order.id.toString().includes(searchTerm);

    // Date range filter
    let matchDateFilter = true;
    if (dateFrom || dateTo) {
      const orderDate = new Date(order.createdAt);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchDateFilter = matchDateFilter && orderDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire day
        matchDateFilter = matchDateFilter && orderDate <= toDate;
      }
    }

    // Area filter
    const matchAreaFilter = areaFilter === 'all' || (order.area || '').toLowerCase() === areaFilter.toLowerCase();

    return matchStatusFilter && matchSearch && matchDateFilter && matchAreaFilter;
  });

  // Sort orders
  filteredOrders = [...filteredOrders].sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'date-desc':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'total-asc':
        return (a.totalPrice || 0) - (b.totalPrice || 0);
      case 'total-desc':
        return (b.totalPrice || 0) - (a.totalPrice || 0);
      default:
        return 0;
    }
  });

  const getTotalRevenue = () => {
    return filteredOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (filteredOrders.length === 0) {
      setToast({ message: 'No orders to export', type: 'error' });
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // 'l' for landscape
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 15;

    // Title
    doc.setFontSize(16);
    doc.text('Corn Bliss - Order Report', 14, yPosition);
    yPosition += 10;

    // Report date and basic info
    doc.setFontSize(10);
    doc.text(`Report Generated: ${new Date().toLocaleDateString('en-US')}`, 14, yPosition);
    yPosition += 5;
    doc.text(`Total Orders: ${filteredOrders.length}`, 14, yPosition);
    yPosition += 8;

    // Filters section
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Applied Filters:', 14, yPosition);
    yPosition += 6;

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    const filterDetailsX = 20;
    
    // Status filter
    if (filter !== 'all') {
      doc.text(`Status: ${filter.charAt(0).toUpperCase() + filter.slice(1)}`, filterDetailsX, yPosition);
      yPosition += 4;
    }
    
    // Search term
    if (searchTerm) {
      doc.text(`Search: ${searchTerm}`, filterDetailsX, yPosition);
      yPosition += 4;
    }
    
    // Date range
    if (dateFrom || dateTo) {
      const dateRange = `${dateFrom || 'N/A'} to ${dateTo || 'N/A'}`;
      doc.text(`Date Range: ${dateRange}`, filterDetailsX, yPosition);
      yPosition += 4;
    }
    
    // Area filter
    if (areaFilter !== 'all') {
      doc.text(`Area: ${areaFilter}`, filterDetailsX, yPosition);
      yPosition += 4;
    }
    
    // Sort by
    const sortLabels = {
      'date-desc': 'Latest First',
      'date-asc': 'Oldest First',
      'total-desc': 'Highest Total',
      'total-asc': 'Lowest Total'
    };
    doc.text(`Sort By: ${sortLabels[sortBy] || sortBy}`, filterDetailsX, yPosition);
    yPosition += 8;

    // Table headers with selected columns
    const headers = ['Order ID', 'Date', 'Customer', 'Phone', 'Address', 'Area', 'Items', 'Total', 'Payment', 'Status', 'Completed'];
    const columnWidths = [13, 16, 22, 18, 32, 16, 50, 15, 16, 15, 18];
    let xPosition = 14;

    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    
    // Draw header background
    doc.setFillColor(102, 126, 234);
    doc.rect(14, yPosition - 5, pageWidth - 28, 7, 'F');
    doc.setTextColor(255, 255, 255);
    
    headers.forEach((header, idx) => {
      doc.text(header, xPosition + 1, yPosition, { maxWidth: columnWidths[idx] - 2 });
      xPosition += columnWidths[idx];
    });
    
    yPosition += 8;
    doc.setTextColor(0, 0, 0);

    // Table data
    doc.setFont(undefined, 'normal');
    doc.setFontSize(7);

    const cellHeight = 10;

    filteredOrders.forEach((order, idx) => {
      const orderDate = formatDate(order.createdAt);
      const status = (order.status || 'Pending').charAt(0).toUpperCase() + (order.status || 'Pending').slice(1);
      const completedDate = order.completedAt ? formatDate(order.completedAt) : 'N/A';
      const itemsList = (order.items || []).map(item => `${item.productName} x${item.quantity}`).join(', ');
      
      if (yPosition + cellHeight > pageHeight - 10) {
        doc.addPage();
        yPosition = 15;
        
        // Repeat header on new page
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(102, 126, 234);
        doc.rect(14, yPosition - 5, pageWidth - 28, 7, 'F');
        doc.setTextColor(255, 255, 255);
        
        xPosition = 14;
        headers.forEach((header, idx) => {
          doc.text(header, xPosition + 1, yPosition, { maxWidth: columnWidths[idx] - 2 });
          xPosition += columnWidths[idx];
        });
        
        yPosition += 12;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(7);
      }

      // Draw row background
      if (idx % 2 === 0) {
        doc.setFillColor(245, 245, 245);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(14, yPosition - 5.5, pageWidth - 28, cellHeight, 'F');

      // Draw row border
      doc.setDrawColor(200, 200, 200);
      doc.rect(14, yPosition - 5.5, pageWidth - 28, cellHeight);

      xPosition = 14;
      const cellTextY = yPosition + 1;
      
      // Helper function to safely render text within column width
      const renderCell = (text, width) => {
        const cellText = String(text || '').substring(0, 60);
        doc.text(cellText, xPosition + 1.5, cellTextY, { maxWidth: width - 3, overflow: 'hidden' });
        xPosition += width;
      };
      
      // Order ID
      renderCell(`#${order.id}`, columnWidths[0]);
      
      // Date
      renderCell(orderDate, columnWidths[1]);
      
      // Customer
      renderCell(order.customerName || '', columnWidths[2]);
      
      // Phone
      renderCell(order.phone1 || '', columnWidths[3]);
      
      // Address
      renderCell(order.address || '', columnWidths[4]);
      
      // Area
      renderCell(order.area || '', columnWidths[5]);
      
      // Items
      renderCell(itemsList, columnWidths[6]);
      
      // Total
      renderCell(`Rs. ${(order.totalPrice || 0).toLocaleString()}`, columnWidths[7]);
      
      // Payment Method
      renderCell(order.paymentMethod || 'Cash on Delivery', columnWidths[8]);
      
      // Status
      renderCell(status, columnWidths[9]);
      
      // Completed
      renderCell(completedDate, columnWidths[10]);

      yPosition += cellHeight + 0.5;
    });

    // Save PDF
    doc.save(`corn-bliss-orders-${new Date().toISOString().split('T')[0]}.pdf`);
    setToast({ message: `PDF exported with ${filteredOrders.length} orders`, type: 'success' });
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      setToast({ message: 'No orders to export', type: 'error' });
      return;
    }
    
    // Prepare filter summary data
    const sortLabels = {
      'date-desc': 'Latest First',
      'date-asc': 'Oldest First',
      'total-desc': 'Highest Total',
      'total-asc': 'Lowest Total'
    };
    

    // Prepare data for Excel
    const excelData = filteredOrders.map(order => ({
      'Order ID': order.id,
      'Date': formatDate(order.createdAt),
      'Time': formatTime(order.createdAt),
      'Customer Name': order.customerName || '',
      'Email': order.email || '',
      'Phone 1': order.phone1 || '',
      'Phone 2': order.phone2 || '',
      'Address': order.address || '',
      'Area': order.area || '',
      'Delivery Type': order.deliveryType || '',
      'Delivery Date': order.deliveryDate ? formatDate(order.deliveryDate) : '',
      'Payment Method': order.paymentMethod || 'Cash on Delivery',
      'Items': (order.items || []).map(item => `${item.productName} x${item.quantity}`).join(', '),
      'Total Price': order.totalPrice || 0,
      'Status': (order.status || 'Pending').charAt(0).toUpperCase() + (order.status || 'Pending').slice(1),
      'Completed At': order.completedAt ? formatDate(order.completedAt) : 'N/A',
      'Notes': order.notes || '',
      'Special Instructions': order.specialInstructions || '',
      'Created At': formatDate(order.createdAt) + ' ' + formatTime(order.createdAt),
      'Updated At': order.updatedAt ? (formatDate(order.updatedAt) + ' ' + formatTime(order.updatedAt)) : '',
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Add Orders sheet with filter info at top
    // const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Merge the filter info with order data by creating a combined array
    const combinedData = [
      ['Export Report'],
      [''],
      ['Report Generated:', new Date().toLocaleDateString('en-US')],
      ['Total Orders:', filteredOrders.length],
      [''],
      ['Applied Filters:'],
      ['Status Filter:', filter !== 'all' ? filter.charAt(0).toUpperCase() + filter.slice(1) : 'All'],
      ['Search Term:', searchTerm || 'None'],
      ['Date Range:', (dateFrom || dateTo) ? `${dateFrom || 'N/A'} to ${dateTo || 'N/A'}` : 'All'],
      ['Area Filter:', areaFilter !== 'all' ? areaFilter : 'All'],
      ['Sort By:', sortLabels[sortBy] || sortBy],
      [''],
      [''],
    ];
    
    // Create Orders sheet with filters at top
    const wsOrders = XLSX.utils.aoa_to_sheet(combinedData);
    
    // Get the order headers
    const orderHeaders = Object.keys(excelData[0]);
    
    // Add data with headers (sheet_add_json adds headers automatically)
    XLSX.utils.sheet_add_json(wsOrders, excelData, { origin: 'A14', header: orderHeaders });

    // Set column widths
    const columnWidths = [
      { wch: 10 },  // Order ID
      { wch: 12 },  // Date
      { wch: 10 },  // Time
      { wch: 20 },  // Customer Name
      { wch: 25 },  // Email
      { wch: 15 },  // Phone 1
      { wch: 15 },  // Phone 2
      { wch: 30 },  // Address
      { wch: 15 },  // Area
      { wch: 15 },  // Delivery Type
      { wch: 12 },  // Delivery Date
      { wch: 45 },  // Items
      { wch: 15 },  // Total Price
      { wch: 12 },  // Status
      { wch: 15 },  // Completed At
      { wch: 20 },  // Notes
      { wch: 25 },  // Special Instructions
      { wch: 20 },  // Created At
      { wch: 20 },  // Updated At
    ];
    wsOrders['!cols'] = columnWidths;
    
    // Add Orders sheet to workbook
    XLSX.utils.book_append_sheet(wb, wsOrders, 'Orders');

    // Save Excel file
    XLSX.writeFile(wb, `corn-bliss-orders-${new Date().toISOString().split('T')[0]}.xlsx`);
    setToast({ message: `Excel file exported with ${filteredOrders.length} orders`, type: 'success' });
  };

  // Export simplified summary report with 2 columns
  const exportToExcelSummary = () => {
    if (filteredOrders.length === 0) {
      setToast({ message: 'No orders to export', type: 'error' });
      return;
    }

    // Prepare filter summary data
    const sortLabels = {
      'date-desc': 'Latest First',
      'date-asc': 'Oldest First',
      'total-desc': 'Highest Total',
      'total-asc': 'Lowest Total'
    };

    // Create summary data with 2 columns
    const summaryData = filteredOrders.map(order => ({
      'Customer Information': `${order.customerName || ''}, ${order.address || ''}, ${order.phone1 || ''}${order.phone2 ? ', ' + order.phone2 : ''}`,
      'Items & Details': `${(order.items || []).map(item => `${item.productName} (Qty: ${item.quantity})`).join(' | ')}, Address: ${order.address || 'N/A'}, Phone: ${order.phone1 || 'N/A'}`,
    }));

    // Merge the filter info with order data by creating a combined array
    const combinedData = [
      ['Orders Summary Report'],
      [''],
      ['Report Generated:', new Date().toLocaleDateString('en-US')],
      ['Total Orders:', filteredOrders.length],
      [''],
      ['Applied Filters:'],
      ['Status Filter:', filter !== 'all' ? filter.charAt(0).toUpperCase() + filter.slice(1) : 'All'],
      ['Search Term:', searchTerm || 'None'],
      ['Date Range:', (dateFrom || dateTo) ? `${dateFrom || 'N/A'} to ${dateTo || 'N/A'}` : 'All'],
      ['Area Filter:', areaFilter !== 'all' ? areaFilter : 'All'],
      ['Sort By:', sortLabels[sortBy] || sortBy],
      [''],
      [''],
    ];

    // Create Orders sheet with filters at top
    const wsSummary = XLSX.utils.aoa_to_sheet(combinedData);

    // Add data with headers
    XLSX.utils.sheet_add_json(wsSummary, summaryData, { origin: 'A14', header: ['Customer Information', 'Items & Details'] });

    // Set column widths for 2 columns
    const columnWidths = [
      { wch: 50 },  // Customer Information
      { wch: 80 },  // Items & Details
    ];
    wsSummary['!cols'] = columnWidths;

    // Create workbook and add sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Save Excel file
    XLSX.writeFile(wb, `corn-bliss-orders-summary-${new Date().toISOString().split('T')[0]}.xlsx`);
    setToast({ message: `Summary report exported with ${filteredOrders.length} orders`, type: 'success' });
  };

  if (loading) {
    return (
      <div className="admin-panel-fullpage">
        <div className="admin-loading-fullpage">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-fullpage">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <EditOrderModal 
        order={selectedOrder} 
        isOpen={isModalOpen} 
        onClose={handleModalClose} 
        onSave={handleSaveOrder}
      />

      <NewOrderModal
        isOpen={isNewOrderModalOpen}
        onClose={handleNewOrderModalClose}
        onSubmit={handleCreateOrder}
      />

      {/* ── Header ── */}
      <div className="admin-header-fullpage">
        <div className="admin-header-left">
          <h1>Admin Panel - Order Management</h1>
          <p className="admin-subtitle">View and manage all customer orders</p>
        </div>
        <div className="admin-header-info">
          <span className="admin-username">👤 {username}</span>
        </div>
        <div className="admin-header-buttons">
          <button className="btn-new-order-fullpage" onClick={handleNewOrderClick} title="Create new order">
            ➕ New Order
          </button>
          <button className="btn-refresh-fullpage" onClick={fetchOrders} title="Refresh orders">
            🔄 Refresh
          </button>
          {filteredOrders.length > 0 && (
            <>
              <button className="btn-export-pdf" onClick={exportToPDF} title={`Export ${filteredOrders.length} order(s) to PDF`}>
                📄 Export PDF
              </button>
              <button className="btn-export-excel" onClick={exportToExcel} title={`Export ${filteredOrders.length} order(s) to Excel`}>
                📊 Export Excel
              </button>
              <button className="btn-export-excel-summary" onClick={exportToExcelSummary} title={`Export ${filteredOrders.length} order(s) summary to Excel`}>
                📋 Export Summary
              </button>
            </>
          )}
          <button className="btn-logout-fullpage" onClick={handleLogout} title="Logout">
            🚪 Logout
          </button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="admin-stats-fullpage">
        <div className="stat-card-fullpage">
          <span className="stat-label">Total Orders</span>
          <span className="stat-value">{filteredOrders.length}</span>
        </div>
        <div className="stat-card-fullpage highlight-stat">
          <span className="stat-label">Filtered Revenue</span>
          <span className="stat-value">Rs. {getTotalRevenue().toLocaleString()}</span>
        </div>
        <div className="stat-card-fullpage">
          <span className="stat-label">Pending</span>
          <span className="stat-value">{filteredOrders.filter(o => (o.status || '').toLowerCase() === 'pending').length}</span>
        </div>
        <div className="stat-card-fullpage">
          <span className="stat-label">Processing</span>
          <span className="stat-value">{filteredOrders.filter(o => (o.status || '').toLowerCase() === 'processing').length}</span>
        </div>
        <div className="stat-card-fullpage">
          <span className="stat-label">Completed</span>
          <span className="stat-value">{filteredOrders.filter(o => (o.status || '').toLowerCase() === 'completed').length}</span>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="admin-controls-fullpage">
        <div className="controls-left">
          <div className="search-box">
            <input 
              type="text" 
              placeholder="Search by name, email, phone, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>

          <div className="filters-tabs-fullpage">
            {['all', 'pending', 'processing', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                className={`filter-tab-fullpage ${filter === status ? 'active' : ''}`}
                onClick={() => setFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="controls-middle">
          <label className="date-filter-label">
            <span>From:</span>
            <input 
              type="date" 
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
          </label>
          <label className="date-filter-label">
            <span>To:</span>
            <input 
              type="date" 
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </label>

          <select 
            value={areaFilter} 
            onChange={(e) => setAreaFilter(e.target.value)} 
            className="area-select"
          >
            <option value="all">All Areas</option>
            <option value="Gampaha District">Gampaha District</option>
            <option value="Negombo">Negombo</option>
            <option value="Kalutara">Kalutara</option>
            <option value="Panadura">Panadura</option>
            <option value="Badulla Town">Badulla Town</option>
          </select>

          <button className="btn-clear-filters" onClick={handleClearFilters} title="Clear all filters">
            ✕ Clear Filters
          </button>
        </div>

        <div className="controls-right">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="date-desc">Latest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="total-desc">Highest Total</option>
            <option value="total-asc">Lowest Total</option>
          </select>
        </div>
      </div>

      {/* ── Error State ── */}
      {error && (
        <div className="admin-error-fullpage">
          <p>{error}</p>
          <button onClick={fetchOrders} className="btn-retry">Retry</button>
        </div>
      )}

      {/* ── Empty State ── */}
      {filteredOrders.length === 0 && !error && (
        <div className="admin-empty-fullpage">
          <span className="empty-icon">📦</span>
          <h3>No Orders Found</h3>
          <p>{searchTerm ? 'No orders match your search' : 'There are no orders at the moment.'}</p>
        </div>
      )}

      {/* ── Bulk Actions ── */}
      {filteredOrders.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            {selectedOrderIds.size > 0 && (
              <span>{selectedOrderIds.size} order(s) selected</span>
            )}
          </div>
          <div className="bulk-buttons">
            {selectedOrderIds.size > 0 && (
              <button 
                className="btn-bulk-delete" 
                onClick={handleBulkDelete}
                title="Delete selected orders"
              >
                🗑️ Delete Selected ({selectedOrderIds.size})
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Orders Table ── */}
      {filteredOrders.length > 0 && (
        <div className="admin-table-container-fullpage">
          <table className="orders-table-fullpage">
            <thead>
              <tr>
                <th className="col-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedOrderIds.size === filteredOrders.length && filteredOrders.length > 0}
                    onChange={handleSelectAll}
                    title="Select all visible orders"
                  />
                </th>
                <th className="col-order-id">Order ID</th>
                <th className="col-date">Date</th>
                <th className="col-customer">Customer</th>
                <th className="col-contact">Contact</th>
                <th className="col-address">Address</th>
                <th className="col-area">Area</th>
                <th className="col-items">Items</th>
                <th className="col-total">Total</th>
                <th className="col-payment">Payment</th>
                <th className="col-status">Status</th>
                <th className="col-delivery">Delivery</th>
                <th className="col-completed">Completed</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr className="order-row">
                    <td className="col-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedOrderIds.has(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                      />
                    </td>
                    <td className="col-order-id">
                      <span className="order-id-badge">#{order.id}</span>
                    </td>
                    <td className="col-date">
                      <div className="date-time">
                        <div className="date">{formatDate(order.createdAt)}</div>
                        <div className="time">{formatTime(order.createdAt)}</div>
                      </div>
                    </td>
                    <td className="col-customer">
                      <div className="customer-info">
                        <div className="customer-name">{order.customerName}</div>
                        <div className="customer-email">{order.email}</div>
                      </div>
                    </td>
                    <td className="col-contact">
                      <div className="contact-info">
                        <div className="phone">{order.phone1}</div>
                        {order.phone2 && <div className="phone-alt">{order.phone2}</div>}
                      </div>
                    </td>
                    <td className="col-address">
                      <span className="address-text">{order.address}</span>
                    </td>
                    <td className="col-area">
                      <span className="area-text">{order.area || 'N/A'}</span>
                    </td>
                    <td className="col-items">
                      <div className="items-list-simple">
                        {(order.items || []).map((item, idx) => (
                          <div key={idx} className="item-simple">
                            <span className="item-name">{item.productName}</span>
                            <span className="item-qty">x{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="col-total">
                      <div className="total-breakdown">
                        <div className="total-item">
                          <span className="total-label">Subtotal:</span>
                          <span>Rs. {(order.subtotalPrice || order.totalPrice || 0).toLocaleString()}</span>
                        </div>
                        {order.deliveryCharge > 0 && (
                          <div className="total-item">
                            <span className="total-label">Delivery:</span>
                            <span>Rs. {(order.deliveryCharge || 0).toLocaleString()}</span>
                          </div>
                        )}
                        <div className="total-item total-final">
                          <span className="total-label">Total:</span>
                          <span className="total-price">Rs. {(order.totalPrice || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-payment">
                      <span className="payment-badge">{order.paymentMethod || 'Cash on Delivery'}</span>
                    </td>
                    <td className="col-status">
                      <span className={`status-badge ${getStatusColor(order.status)}`}>
                        {(order.status || 'Pending').charAt(0).toUpperCase() + (order.status || 'Pending').slice(1)}
                      </span>
                    </td>
                    <td className="col-delivery">
                      <span className="delivery-type">{order.deliveryType}</span>
                    </td>
                    <td className="col-completed">
                      {order.completedAt ? (
                        <div className="completed-date">
                          <div className="date">{formatDate(order.completedAt)}</div>
                          <div className="time">{formatTime(order.completedAt)}</div>
                        </div>
                      ) : (
                        <span className="not-completed">-</span>
                      )}
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons">
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEditClick(order)}
                          title="Edit order"
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="btn-delete" 
                          onClick={() => handleDeleteClick(order.id)}
                          title="Delete order"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      <EditOrderModal 
        order={selectedOrder} 
        isOpen={isModalOpen} 
        onClose={handleModalClose} 
        onSave={handleSaveOrder}
      />

      <NewOrderModal
        isOpen={isNewOrderModalOpen}
        onClose={handleNewOrderModalClose}
        onSubmit={handleCreateOrder}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title="Delete Order"
        message="Are you sure you want to delete this order? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ConfirmationModal
        isOpen={bulkDeleteConfirmation}
        title="Delete Multiple Orders"
        message={`Are you sure you want to delete ${selectedOrderIds.size} order(s)? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={handleConfirmBulkDelete}
        onCancel={handleCancelBulkDelete}
      />
    </div>
  );
}

export default AdminPanel;
